import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import butterflyApi from '../ButterflyApi';
import MobilePage from '../UiComponents/MobilePage';
import { isLogin } from '../Utils/Cookies';
import { callName } from '../Utils/Strings';
import { getAppProductionStatusText, replaceAppTitleCallName } from './appProductionStatus';
import './Orders.css';

const formatPrice = (price) => {
  if (price === undefined || price === null) return ''
  return `${new Intl.NumberFormat('ko-KR').format(price)}원`
}

const formatDate = (datetime) => {
  if (!datetime) return ''
  return datetime.replace('T', ' ')
}

const normalizeProductions = (response) => {
  if (!response) return []
  if (Array.isArray(response)) return response
  return response.items || []
}

const getLocalHeroes = () => {
  try {
    return JSON.parse(localStorage.getItem('heroes')) || []
  } catch (e) {
    return []
  }
}

const replaceCallName = (text, heroCallName) => {
  return (text || '').replace(/\{callName\}/g, heroCallName)
}

const getHeroCallName = (production, heroes) => {
  const hero = heroes.find(hero => `${hero.heroNo}` === `${production.heroNo}`)
  if (hero) return callName(hero.firstName)
  if (production.heroFirstName) return callName(production.heroFirstName)
  if (production.firstName) return callName(production.firstName)
  if (production.heroName) return callName(production.heroName)
  return '하늘이'
}

const getProductionAppName = (production, heroCallName) => {
  return replaceAppTitleCallName(production.app?.title || production.app?.appName || production.title || production.appName, production, heroCallName)
}

const getProductionAppDescription = (production, heroCallName) => {
  return replaceCallName(production.app?.description || production.appDescription || production.description, heroCallName)
}

const AppProductionList = () => {
  const [productions, setProductions] = useState([])
  const [loading, setLoading] = useState(true)
  const heroes = getLocalHeroes()

  const load = useCallback(async () => {
    if (!isLogin()) {
      setLoading(false)
      return
    }

    setLoading(true)
    const response = await butterflyApi.getAppProductions()
    setProductions(normalizeProductions(response))
    setLoading(false)
  }, [])

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 })
    load()
  }, [load])

  if (loading) {
    return <MobilePage className='storybook-page orders-page' loading loadingMessage="주문내역을 불러오고 있습니다." />
  }

  return <MobilePage className='storybook-page orders-page'>
    <main className='orders-content'>
      <Link className='book-text-link orders-home' to='/'><span aria-hidden='true'>‹</span> 홈으로</Link>
      <header className='orders-intro'>
        <p className='book-eyebrow'><span aria-hidden='true'>✦</span> 우리 아이를 위한 특별한 선물</p>
        <div className='orders-title-row'>
          <h1 className='book-title'>주문내역</h1>
          <img src='/image/main/books.png' alt='' width='60' height='60' />
        </div>
        <p className='book-description'>신청한 동화와 게임을 모아봤어요.<br />우리 아이의 선물이 완성되는 과정을 확인해 보세요.</p>
      </header>

      {
        !isLogin() && <section className='orders-empty' aria-labelledby='orders-login-title'>
          <span className='orders-empty-icon' aria-hidden='true'>✧</span>
          <h2 id='orders-login-title'>우리 아이의 선물을 확인해 보세요</h2>
          <p>로그인하면 신청한 동화와 게임의<br />주문내역과 제작 상태를 볼 수 있어요.</p>
          <Link className='book-primary-button' to='/login?redirect=/orders'>로그인하기 <span aria-hidden='true'>→</span></Link>
        </section>
      }

      {
        isLogin() && productions.length === 0 && <section className='orders-empty' aria-labelledby='orders-empty-title'>
          <span className='orders-empty-icon' aria-hidden='true'>✧</span>
          <h2 id='orders-empty-title'>아직 주문내역이 없어요</h2>
          <p>우리 아이가 주인공이 되는 특별한 이야기.<br />첫 번째 선물을 골라볼까요?</p>
          <Link className='book-primary-button' to='/apps?category=fairytale'>동화 둘러보기 <span aria-hidden='true'>→</span></Link>
          <Link className='book-text-link' to='/'>홈으로 이동하기 <span aria-hidden='true'>↗</span></Link>
        </section>
      }

      {
        isLogin() && productions.length > 0 && <section className='orders-section' aria-labelledby='orders-list-title'>
          <div className='orders-section-heading'>
            <div>
              <h2 id='orders-list-title'>나의 주문</h2>
              <p>카드를 눌러 자세한 내용을 확인해 보세요.</p>
            </div>
            <span className='orders-count'>{productions.length}건</span>
          </div>
          <ul className='orders-list'>
            {productions.map(production => {
              const heroCallName = getHeroCallName(production, heroes)
              const appName = getProductionAppName(production, heroCallName)
              const appDescription = getProductionAppDescription(production, heroCallName)
              const statusText = getAppProductionStatusText(production.productionStatus)

              return <li key={production.productionNo}>
                <Link
                  className='orders-card'
                  to={`/orders/${production.productionNo}`}
                  aria-label={`${appName}${statusText ? `, ${statusText}` : ''}, 주문 상세보기`}
                >
                  {(statusText || production.heroName) && <div className='orders-card-meta'>
                    {statusText && <span className='orders-status' data-status={production.productionStatus}>
                      <span className='orders-status-dot' aria-hidden='true' />{statusText}
                    </span>}
                    {production.heroName && <span className='orders-hero'>주인공 <strong>{production.heroName}</strong></span>}
                  </div>}
                  <h3>{appName}</h3>
                  {appDescription && <p className='orders-card-description'>{appDescription}</p>}
                  <dl className='orders-card-details'>
                    <div>
                      <dt>주문일시</dt>
                      <dd>{production.registerDatetime ? <time dateTime={production.registerDatetime}>{formatDate(production.registerDatetime)}</time> : '—'}</dd>
                    </div>
                    <div className='orders-card-amount'>
                      <dt>결제 금액</dt>
                      <dd>{formatPrice(production.amount) || '—'}</dd>
                    </div>
                  </dl>
                  <div className='orders-card-action'>
                    주문 상세보기 <span aria-hidden='true'>→</span>
                  </div>
                </Link>
              </li>
            })}
          </ul>
        </section>
      }

      {isLogin() && productions.length > 0 && <aside className='orders-guide' aria-labelledby='orders-guide-title'>
        <span className='book-eyebrow'><span aria-hidden='true'>✧</span> 선물을 기다리는 동안</span>
        <h2 id='orders-guide-title'>제작 진행이 궁금하신가요?</h2>
        <p>주문 상세에서 제작 상태와 완성된 결과를 확인해요.<br />추가로 궁금한 점은 1:1 문의로 남겨주세요.</p>
        <Link className='book-text-link' to='/support/inquiries'>1:1 문의하기 <span aria-hidden='true'>↗</span></Link>
      </aside>}
      <p className='orders-footnote'><span aria-hidden='true'>✦</span> 아이를 위한 마음을, 특별한 선물로 담아요.</p>
    </main>
  </MobilePage>
}

export default AppProductionList
